import { isStageVariable } from '../constants/stage-variables'
import { all, create } from 'mathjs'

/**
 * Shared mathjs instance used by every formula evaluator and the formula editor.
 *
 * Three overrides on top of stock mathjs:
 *
 *  1. `if(cond, then, else)` — mathjs has no native `if`. Defined with
 *     `rawArgs = true` so the branches are LAZILY evaluated: an admin can
 *     safely write `if(isset(x), x * 100, 0)` and the `x * 100` branch is
 *     only computed when `x` is non-null (otherwise mathjs would throw at
 *     `null * 100` before `if` sees the condition).
 *  2. `equal` / `unequal` — stock mathjs coerces both sides to number, so
 *     `'WOOD' == 'STEEL'` throws. We need plain JS strict equality so OPTION-mode
 *     variables (selected option keys, energy labels, property type/subType)
 *     can be compared to string literals in formulas.
 *  3. `isset(x)` — returns `1` when `x` is defined (anything except `null` or
 *     `undefined`) and `0` otherwise. Used by admins to gate options with
 *     "Aktiviert-wenn"/"Deaktiviert-wenn" checks on HouseElementType keys
 *     whose project value may be null. Combine with `not(isset(x))` for the
 *     negative case.
 *
 * Effect on numeric comparisons: `100 == 100` still returns true.
 * Effect on cross-type comparisons: `5 == '5'` now returns false (was true).
 * Effect on arrays: reference equality (was element-wise). Formulas never
 * receive arrays, so this is irrelevant in practice.
 */
export const math = create(all)

// Lazy `if`. mathjs `rawArgs=true` funcs receive the raw AST nodes plus the
// evaluator + scope so they can decide which branches to evaluate.
const lazyIf = function (
  args: Array<{ evaluate: (scope: any) => unknown }>,
  _math: unknown,
  scope: any,
) {
  const cond = args[0].evaluate(scope)
  return cond ? args[1].evaluate(scope) : args[2].evaluate(scope)
} as unknown as ((...a: unknown[]) => unknown) & { rawArgs: boolean }
lazyIf.rawArgs = true

math.import(
  {
    if: lazyIf,
    equal: (a: unknown, b: unknown) => a === b,
    unequal: (a: unknown, b: unknown) => a !== b,
    isset: (value: unknown) => (value === undefined || value === null ? 0 : 1),
  },
  { override: true },
)

/** A formula name, as an identifier of the expression language. */
export const FORMULA_NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/

/**
 * Whether the expression language already binds this name.
 *
 * A formula is put into the scope under its own name, and formula names are
 * applied last — one called `min` or `abs` therefore replaces the function
 * every other expression calls, each of those throws, and the evaluator
 * swallows the throw and returns null: prices across the configurator read
 * "Auf Anfrage" with nothing to point at. Asked of the instance itself rather
 * than a hand-kept list, so an upgrade that adds a function is covered.
 *
 * The staged calculation's own names are reserved beside them: it writes
 * `subtotal` and `stage_<n>` into the scope a stage's expressions run in, and
 * a formula of that name would be read as one stage's subtotal in some
 * expressions and as itself in others.
 */
export function isReservedFormulaName(name: string): boolean {
  return (
    Object.prototype.hasOwnProperty.call(math, name) || isStageVariable(name)
  )
}
